import {
  colors, CTAPrimary,
  GlobalStyle,
  GoodSansB2, GoodSansH1, OutlineOnFocusVisible,
  QueensH2,
  QueensH3
} from '@components/shared'
import HtmlHead from '@components/HtmlHead'
import * as React from 'react'
import BackgroundGradient from '@components/BackgroundGradient'
import TopNav from '@components/TopNav'
import styled from 'styled-components'
import { Col, config, Container, Row } from 'react-awesome-styled-grid'
import { FadeInImage } from '@components/shared/FadeInImage'
import { useDispatch } from 'react-redux'
import { playAudio } from '../actions/audioActions'
import type { PlayType } from '@services/audio'
import { getDurationText } from '../models/helpers'
import { identifyUserWithCallback, trackSegment } from '@services/segment'

const AIRTABLE_BASE_ID = 'appphciSEw1TFN8ce'
const AIRTABLE_TABLE_NAME = 'tblhXNhA4BMLhaRS0' // Or use Table ID
const AIRTABLE_API_KEY = 'patT23SwzH8pm891F.8b521bae0f6940924fb15c803ad679dce728087a48644460d2a53f35456e48d6'

async function submitToAirtable (dataToSend: {
  fields: {
    title: string,
    email: string,
    question1: string,
    question2: string,
    question3: string
  }
}): Promise<boolean> {
  try {
    const response = await fetch(
      `https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/${encodeURIComponent(AIRTABLE_TABLE_NAME)}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${AIRTABLE_API_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSend)
      }
    )

    if (response.ok) {
      return true
    } else {
      const errorData = await response.json()
      console.error('Airtable API Error:', errorData)
      return false
    }
  } catch (error) {
    console.error('Fetch Error:', error)
    return false
  }
}

function submitEventToSegment (dataToSend: {
  fields: {
    title: string,
    email: string,
    question1: string,
    question2: string,
    question3: string
  }
}): void {
  identifyUserWithCallback(undefined, dataToSend.fields.email, (): void => {
    trackSegment('Sessions Live 2025 Submission', dataToSend.fields)
  })
}

type ChapterType = PlayType & {tags: string[]}

const chapters: ChapterType[] = [
  {
    id: '',
    audioUrl: 'https://api.dipseastories.com/audio/600_220526_PlusOne1.mp3',
    title: 'Plus One',
    duration: 965,
    slug: null,
    description: 'Eva needs a date to a wedding to make her ex jealous, and Jay is more than willing to play along.',
    imageUrl: 'https://api.dipseastories.com/images/500-600_PlusOneI_SuzanneDias-min.jpg',
    tags: ['Her + Them', 'Fake Dating', 'Romantic']
  },
  {
    id: '',
    audioUrl: 'https://api.dipseastories.com/audio/757_230428_GodsandMen1.mp3',
    title: 'Gods & Men',
    duration: 882,
    slug: null,
    description: 'As Apollo teaches Hyacinthus how to use his bow and arrow, a spark ignites between them.',
    imageUrl: 'https://api.dipseastories.com/images/1000-MA_26_MoanAudioApolloAndHyacinthusCompressed_v2_Midjourney.png',
    tags: ['Him + Him', 'Greek Mythology', 'Friends to Lovers']
  },
  {
    id: '',
    audioUrl: 'https://api.dipseastories.com/audio/729_230210_Frenemies1.mp3',
    title: 'Frenemies',
    duration: 607,
    slug: null,
    description: 'When Adrianne and Zoe reconnect at a party, a heated conversation stirs up unspoken desires.',
    imageUrl: 'https://api.dipseastories.com/images/1000-734_Homecoming2_Midjourney.png',
    tags: ['Her + Her', 'Friends to Lovers', 'First Time']
  },
  {
    id: '',
    audioUrl: 'https://api.dipseastories.com/audio/422_210305_Hometown1.mp3',
    title: 'Hometown',
    duration: 629,
    slug: null,
    description: 'When Jack and Gia cross paths back in their hometown, old feelings come rushing back.',
    imageUrl: 'https://api.dipseastories.com/images/500-422_HometownI_ArielSinha-fs8.png',
    tags: ['Her + Him', 'Second Chance', 'Extra Spicy']
  },
  {
    id: '',
    audioUrl: 'https://api.dipseastories.com/audio/605_220714_TheTicketGreece.mp3',
    title: 'The Ticket',
    duration: 629,
    slug: null,
    description: 'Reese joins Juliette on a yacht in the Mediterranean, where she does exactly as she’s told.',
    imageUrl: 'https://api.dipseastories.com/images/500-605_TheTicket1_BonnieBrown-fs8.png',
    tags: ['Her + Her', 'BDSM', 'Intense Language']
  },
  {
    id: '',
    audioUrl: 'https://api.dipseastories.com/audio/380_201119_BehindClosedDoorsV.mp3',
    title: 'Behind Closed Doors',
    duration: 796,
    slug: null,
    description: 'Married couple Mateo and Jada invite their friend Luke to join their latest hotel rendezvous. ',
    imageUrl: 'https://api.dipseastories.com/images/500-380_BehindClosedDoorsV_JosephineRais-fs8.png',
    tags: ['Her + Him + Him', 'BDSM', 'Intense Language']
  }
]

const toggleIntercom = (hide: boolean): void => {
  if (typeof window === 'undefined') return
  if (typeof window.Intercom === 'undefined') return
  window.Intercom('update', {
    hide_default_launcher: hide
  })
}

export default function EstherConferenceHomework (): React.ReactNode {
  const [storyTitle, setStoryTitle] = React.useState('')
  const dispatch = useDispatch()
  const playTrackAudio = (params: ChapterType): void => {
    setStoryTitle(params.title)
    dispatch(
      playAudio({
        id: params.id,
        title: params.title,
        description: 'Can’t hear the audio? Make sure your device’s sound is on.',
        imageUrl: params.imageUrl,
        slug: params.slug,
        audioUrl: params.audioUrl,
        duration: params.duration,
        showNavBar: false
      })
    )
  }

  React.useEffect((): any => {
    toggleIntercom(true)
    return (): void => {
      toggleIntercom(false)
    }
  }, [])
  return (
   <>
     <GlobalStyle backgroundColor={colors.midnightBlue}/>
    <HtmlHead
      title={`Dipsea | Get lost in a steamy fantasy world`}
      description={
        'Get access to the entire Dipsea library to explore 1,000+ sexy audio stories, soothing sleep scenes, steamy POV role-plays, and more. '
      }
    />
     <TopNav
       isDipseaLogoOnly={true}
       isAssetLoaded={true}
     />
     <BackgroundImageContainer>
       <BackgroundGradient
         quadrant={3}
         gradientColor={colors.midnightBlue}
         gradientPosition='bottom'
         overlayColor='blue'
       />
     </BackgroundImageContainer>
     <Container>
       <Row justify='center'>
         <Col align='center'>
           <Header>
             Welcome to your erotic listening exercise
           </Header>
           <Description>
             Below, you’ll find six erotic audio stories—each includes a short description and tags to help you understand the scenario. Read carefully, then select a story that interests you. For the best experience, we recommend using headphones. If you’re listening on your phone, please make sure the sound is on to hear the audio.
           </Description>
           <Description>
             As you listen, the tension will build, culminating in a sex scene between the characters. If you feel uncomfortable at any point, you’re welcome to pause, choose a different story, or stop listening altogether.
           </Description>
           <Description>
             When you’re ready, take a few minutes to answer the questions about your experience in the reflection section. You’ll reference your responses in tomorrow’s group conversation.
           </Description>
           <Hr/>
         </Col>
       </Row>
       <Row>
         <Col>
           <HomeworkHeader>Choose your story</HomeworkHeader>
           <Subheader>Read the descriptions and select a story. It may take a few seconds for the audio to play. As you listen, notice what thoughts or feelings arise. It may take a few seconds to start playing. Please make sure your sound is on to hear the audio.</Subheader>
         </Col>
       </Row>
       <Row>
         <Col>
           {chapters.map((item: ChapterType, index: number): React.ReactNode => {
             return (<CardItem key={`hositonal-large-track-row-${index}`}>
               <ChapterImageContainer aria-label={`click to play ${item.title}`} onClick={(): void => playTrackAudio(item)}>
                 <ImageContainer>
                   <FadeInImage src={item.imageUrl} width={500} height={500} alt={item.title} />
                 </ImageContainer>
                 <PlayButtonContainer aria-hidden={true}><TriangleIcon {...({} as any)} className={'triangleIcon'}/></PlayButtonContainer>
               </ChapterImageContainer>
               <ChapterDetailContainer>
               <HeaderTitleText>
                 {item.title}
               </HeaderTitleText>
                 <Playtime>
                   Length: {getDurationText(item.duration / 60)}
                 </Playtime>
               <TrackDescription>{item.description}</TrackDescription>
               <TagsContainerMobile>
                 <TagSmallContainer>{item.tags.join(', ')}</TagSmallContainer>
               </TagsContainerMobile>
               <TagsContainerLarge>
                 {item.tags.map((item: string, index: number): React.ReactNode => {
                   return <TagLargeContainer key={`tag-${index}`}><TagPillButton key={`tags-${index}`} $small={true}>{item}</TagPillButton></TagLargeContainer>
                 })}
               </TagsContainerLarge>
               </ChapterDetailContainer>
             </CardItem>)
           })}
           <Hr/>
         </Col>
       </Row>
       <Row>
         <Col>
           <HomeworkHeader>Reflection</HomeworkHeader>
           <Subheader>Take a few moments to write about your listening experience. Once you're done, submit your responses to receive them by email for tomorrow’s discussion.</Subheader>
         </Col>
       </Row>
       <Row>
         <Col>
           <StoryReflection storyTitle={storyTitle}/>
         </Col>
       </Row>
     </Container>
   </>)
}

const Label = styled.label<any>`
  display: block;
  margin-bottom: 12px;
  ${GoodSansB2};
  color: ${colors.sand};
`

const Select = styled.select<any>`
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
  font-family: inherit;
  font-size: inherit;
  max-width: 600px;
  -webkit-appearance: none; /* Remove default Safari styling */
  color: black;
    outline: none;
`

const Option = styled.option<any>`
  font-family: inherit;
  font-size: inherit;
`

const StyledTextArea = styled.textarea<any>`
  width: 100%;
  min-height: 120px;
  background-color: white;
  border: none;
  border-radius: 6px;
  padding: 8px;
  margin-top: 8px;
  box-sizing: border-box;
  font-family: inherit; /* Inherit font from parent */
  font-size: inherit; /* Inherit font size from parent */
  max-width: 600px;
 outline: 2px solid white;
`

const PrivateContainer = styled.div<any>`
    ${GoodSansB2};
    margin-top: 20px;
    display: flex;
    align-items: center;
`

const EmailInputContainer = styled.div<any>`
  margin-top: 10px;
`

const EmailInput = styled.input<any>`
  width: 100%;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #ccc;
  box-sizing: border-box;
  max-width: 600px;
    outline: none;
`

const QuestionDivider = styled.div<any>`
    margin-bottom: 18px;
`

const ReflectionForm = styled.div<any>`
margin-bottom: 48px;
`

const StoryReflection = (props: {storyTitle: string}): React.ReactNode => {
  const [response1, setResponse1] = React.useState('')
  const [response2, setResponse2] = React.useState('')
  const [response3, setResponse3] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [showInfo, setShowInfo] = React.useState(false)
  const [selectedStory, setSelectedStory] = React.useState(props.storyTitle)
  const infoBubbleRef = React.useRef<any>(null)
  React.useEffect((): () => void => {
    const handleClickOutside = (event: any): void => {
      if (infoBubbleRef.current && !infoBubbleRef.current.contains(event.target) && event.target !== document.querySelector('#info-icon')) {
        setShowInfo(false)
      }
    }

    if (showInfo) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showInfo])

  React.useEffect((): void => {
    if (props.storyTitle !== selectedStory) {
      setSelectedStory(props.storyTitle)
    }
  }, [props.storyTitle])

  const handleStoryChange = (event: any): void => {
    setSelectedStory(event.target.value)
  }
  const handleResponseChange = (setter: any): any => (event: any): void => {
    setter(event.target.value)
  }

  const handleEmailChange = (event: any): void => {
    setEmail(event.target.value)
  }

  const handleSubmission = async (): Promise<void> => {
    const dataToSend = {
      fields: {
        title: selectedStory,
        question1: response1,
        question2: response2,
        question3: response3,
        email
      }
    }
    const submission = await submitToAirtable(dataToSend)
    submitEventToSegment(dataToSend)
    if (submission) {
      window.alert('Thanks for submitting your responses! A copy is headed to your inbox.')
    }
  }

  return (
    <Row>
      <Col>
        <ReflectionForm>
      <QuestionDivider>
        <Label htmlFor="story">You listened to:</Label>
        <Select id="story" value={selectedStory} onChange={handleStoryChange}>
          <Option value="">Select a story</Option>
          {chapters.map((item: ChapterType, index: number): React.ReactNode => {
            return <Option key={`option-${index}`} value={item.title}>{item.title}</Option>
          })}
        </Select>
      </QuestionDivider>
      <QuestionDivider>
        <Label htmlFor="feeling">
          How did your chosen story resonate with your personal sense of desire?
        </Label>
        <StyledTextArea
          id="feeling"
          value={response1}
          onChange={handleResponseChange(setResponse1)}
        />
      </QuestionDivider>

      <QuestionDivider>
        <Label htmlFor="moments">What emotions or sensations arose during your listening experience?</Label>
        <StyledTextArea
          id="moments"
          value={response2}
          onChange={handleResponseChange(setResponse2)}
        />
      </QuestionDivider>

      <QuestionDivider>
        <Label htmlFor="shift">Did the story shift or deepen your perspective on intimacy or connection?</Label>
        <StyledTextArea
          id="shift"
          value={response3}
          onChange={handleResponseChange(setResponse3)}
        />
      </QuestionDivider>

      <PrivateContainer>
          Share your email to receive your responses. Everything will remain anonymous.
      </PrivateContainer>
      <QuestionDivider>
        <EmailInputContainer>
          <Label htmlFor="email">Your Email:</Label>
          <EmailInput
            type="email"
            id="email"
            value={email}
            onChange={handleEmailChange}
          />
        </EmailInputContainer>
      </QuestionDivider>
      <QuestionDivider><CTAPrimary onClick={handleSubmission}>Submit</CTAPrimary></QuestionDivider>
        </ReflectionForm>
      </Col>
    </Row>
  )
}

const ChapterImageContainer = styled.a<any>`
    position: relative
`

const BackgroundImageContainer = styled.div<any>`
  height: 70%;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: -1;
`

const TriangleIcon = (): React.ReactNode => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    viewBox='0 0 16 16'
    fill={colors.orange}
    className='triangleIcon'
  >
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M2.63119 0.833655C1.96461 0.460356 1.14258 0.942163 1.14258 1.70615V14.2237C1.14258 15.0032 1.99482 15.483 2.66125 15.0787L13.3906 8.56979C14.0455 8.17249 14.0289 7.21658 13.3605 6.84231L2.63119 0.833655Z'
      fill={colors.orange}
    />
  </svg>
)

const PlayButtonContainer = styled.div<any>`
  border-radius: 24px;
  position: absolute;
  top: 75px;
  left: 75px;
  transform: translate(-50%, -50%);
  cursor: pointer;
  display: flex;
  background-color: ${colors.white};
  align-items: center;
  justify-content: center;  
  width: 32px;
  height: 32px;
  ${OutlineOnFocusVisible};
    svg {
        margin-left: 4px;
        width: 14px;
    }
    ${(props: any): any => config(props).media.lg`
    border-radius: 36px;
    width: 64px;
    height: 64px;
    top: 150px;
    left: 150px;
    svg {
        margin-left: 8px;
        width: 24px;
    }
  `}
`

const ImageContainer = styled.div<any>`
    width: 100px;
    height: 100px;
    position: relative;
    z-index: 0;
    img {
        border-radius: 10px;
    }
    margin-bottom: 8px;
    ${(props: any): any => config(props).media.lg`
    width: 200px;
    height: 200px;
  `}
`

const Header = styled.h1<any>`
  ${QueensH2};
  text-align: center;
  margin: 3.5rem 0 1.2rem;
  color: ${colors.sand};
`
const Description = styled.p<any>`
  ${GoodSansB2};
  text-align: center;
  margin-bottom: 2.2rem;
  color: ${colors.sand};
  max-width: 736px;
`

const Hr = styled.hr<any>`
    width: 100%
`

const HomeworkHeader = styled.h2<any>`
  ${QueensH3};
  text-align: left;
  margin: 1rem 0 1rem;
  color: ${colors.sand};
`

const Subheader = styled.p<any>`
  ${GoodSansB2};
  text-align: left;
  margin-bottom: 2.2rem;
  color: ${colors.sand};
`

const TrackDescription = styled.p<any>`
  ${GoodSansB2};
  text-align: left;
  color: ${colors.sand};
  margin-bottom: 8px;
`

const CardItem = styled.article<any>`
    display: flex;
    flex-direction: row;
    margin-bottom: 28px;
    ${(props: any): any => config(props).media.xs`
  `}
    ${(props: any): any => config(props).media.sm`
  `}
    ${(props: any): any => config(props).media.md`
  `}
    ${(props: any): any => config(props).media.lg`
  `}
    ${(props: any): any => config(props).media.xl`
  `}
`

const HeaderTitleText = styled.p<any>`
 ${GoodSansH1};
    span {
        color: ${colors.sand};
        font-size: 10px;
        margin-left: 8px;
    }
`

const Playtime = styled.p<any>`
 ${GoodSansB2};
  color: ${colors.sand};
  font-size: 10px;
`

const TagsContainerMobile = styled.div<any>`
    display: flex;
    ${(props: any): any => config(props).media.lg`
      display: none;
    `};
`

const TagsContainerLarge = styled.div<any>`
    display: none;
    ${(props: any): any => config(props).media.lg`
    display: flex;
    `};
`

const TagLargeContainer = styled.div<any>`
  margin-right: 8px;
`

const TagSmallContainer = styled.div<any>`
  margin-right: 8px;
  ${GoodSansB2};
    color: ${colors.sand};
`

const ChapterDetailContainer = styled.div<any>`
    display: flex;
    flex-direction: column;
    margin-left: 16px;

`

export const TagPillButton = styled.div<any>`
  ${GoodSansB2};
  font-style: normal;
  font-weight: 700;
  line-height: 150%;
  display: inline-block;
  align-items: center;
  justify-content: center;
  color: ${colors.white};
  border-radius: 100px;
  border: 1px solid ${colors.white};
  &:hover {
    opacity: 0.4;
  }
  a {
    text-decoration: none;
    color: ${colors.white};
  }
  ${(props: any): string => props.$small
  ? `padding: 3px 5px; font-size: 8px;`
  : `padding: 10px 15px; font-size: 12px;`
};

  ${(props: any): any => config(props).media.md`
    font-size: ${(props: any): string => props.$small ? `12px` : `14px`};
  `}
`
